import type {
  IActivityOccurrence,
  IActivityOccurrenceOrGroup,
  IActivityOccurrencesAndGroups,
  IActivityOccurrenceStatus,
  IGroupStatus,
  ITimeOfDay,
} from "@medoma/models/activities";

// Status a row in the activities table can display. For individual occurrences
// it is the occurrence's status; for home visit groups it is the group's status.
// The two vocabularies partly overlap (`ongoing`, `finished`) but each also has
// values the other lacks, so the row status is the union of both.
export type IActivityRowStatus = IActivityOccurrenceStatus | IGroupStatus;

// Sort order for the status column. Similar-meaning statuses across the two
// vocabularies (e.g. `ready`/`planned`) are placed next to each other so rows
// cluster visually.
export const activityRowStatusOrder = [
  "cancelled",
  "expired",
  "notReady",
  "ready",
  "planned",
  "travellingTo",
  "ongoing",
  "finished",
] as const satisfies readonly IActivityRowStatus[];

import {
  activityOccurrenceStatusSchema,
  isEmployeePerformed,
  isGroup,
  timeOfDaySchema,
} from "@medoma/models/activities";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import * as Sentry from "@sentry/react";
import { patientStatusSchema } from "@medoma/models/patients";

const requiredCompetences = (
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup,
): IMedicalCompetence[] | undefined =>
  isEmployeePerformed(activityOccurrenceOrGroup)
    ? activityOccurrenceOrGroup.requiredCompetences
    : undefined;

/**
 * Calculate the status for a group of activity occurrences.
 * Priority order:
 * 1. Visit status if it exists
 * 2. "notReady" if all occurrences are not ready
 * 3. "expired" if any occurrence is expired
 * 4. First occurrence's status as fallback
 */
export const calculateGroupStatus = (
  group: Extract<IActivityOccurrenceOrGroup, { occurrences: unknown }>,
): IActivityRowStatus => {
  if (group.visitStatus) {
    return group.visitStatus;
  }

  // Status tag for group (if all occurrences are not ready, assume group is not ready)
  if (
    group.occurrences.every(
      (occurrence) =>
        occurrence.status === activityOccurrenceStatusSchema.enum.notReady,
    )
  ) {
    return "notReady" as const;
  }

  // Status tag for group (if any occurrences are expired, assume group is expired)
  if (
    group.occurrences.some(
      (occurrence) =>
        occurrence.status === activityOccurrenceStatusSchema.enum.expired,
    )
  ) {
    return "expired" as const;
  }

  if (group.occurrences[0]) {
    return group.occurrences[0].status;
  }

  // Fail safe
  Sentry.captureException(new Error("Group has no occurrences"));
  return "notReady" as const;
};

export const resolveAreaDisplayName = (occurrence?: IActivityOccurrence) => {
  if (!occurrence) {
    return undefined;
  }
  if (!occurrence.patient) {
    return undefined;
  }
  if (occurrence.patient.status === patientStatusSchema.enum.deleted) {
    return undefined;
  }
  return occurrence.patient.area?.displayName;
};

export const resolveTeam = (occurrence?: IActivityOccurrence) => {
  if (!occurrence) {
    return undefined;
  }
  if (!occurrence.patient) {
    return undefined;
  }
  if (occurrence.patient.status === patientStatusSchema.enum.deleted) {
    return undefined;
  }
  return occurrence.patient.team || undefined;
};

const resolvePrimaryResponsible = (occurrence?: IActivityOccurrence) => {
  if (!occurrence) {
    return undefined;
  }
  if (!occurrence.patient) {
    return undefined;
  }
  if (occurrence.patient.status === patientStatusSchema.enum.deleted) {
    return undefined;
  }
  return occurrence.patient.primaryResponsible || undefined;
};

export const getDataFromActivityOccurrencesAndGroups = (
  activityOccurrencesAndGroups: IActivityOccurrencesAndGroups,
) => {
  return activityOccurrencesAndGroups.map((activityOccurrenceOrGroup) => {
    if (isGroup(activityOccurrenceOrGroup)) {
      const group = activityOccurrenceOrGroup;
      return {
        activityOccurrenceOrGroup: group,
        category: group.category,
        timespan: [
          group.start,
          group.end,
          group.occurrences.every(
            ({ timeOfDay }) => timeOfDay === timeOfDaySchema.enum.Any,
          )
            ? timeOfDaySchema.enum.Any
            : timeOfDaySchema.enum.Specific,
        ] as [Date, Date, ITimeOfDay],
        title: group,
        patient: group.occurrences[0]?.patient,
        actors: group,
        status: calculateGroupStatus(group),
        requiredCompetences: requiredCompetences(group),
        areaDisplayName: resolveAreaDisplayName(group.occurrences[0]),
        team: resolveTeam(group.occurrences[0]),
        primaryResponsible: resolvePrimaryResponsible(group.occurrences[0]),
      };
    } else {
      const activityOccurrence = activityOccurrenceOrGroup;
      return {
        activityOccurrenceOrGroup: activityOccurrence,
        category: activityOccurrence.category,
        timespan: [
          activityOccurrence.start,
          activityOccurrence.end,
          activityOccurrence.timeOfDay,
        ] as [Date, Date, ITimeOfDay],
        title: activityOccurrence,
        patient: activityOccurrence.patient,
        areaDisplayName: resolveAreaDisplayName(activityOccurrence),
        team: resolveTeam(activityOccurrence),
        actors: activityOccurrence,
        status: activityOccurrence.status,
        requiredCompetences: requiredCompetences(activityOccurrence),
        primaryResponsible: resolvePrimaryResponsible(activityOccurrence),
      };
    }
  });
};
